import axios from 'axios';
import type { CategoriesGroupResponse, CategoriesParams, CategoriesResponse, Category, CategoryDetailResponse } from '../types/Category';
import { baseUrl, HttpMethods } from './base';

const categoryBasePath = `${baseUrl.SHERPA}/categories`;
const categoryV2BasePath = `${baseUrl.SHERPA}/categories-v2`;
const categoryGroupBasePath = `${baseUrl.SHERPA}/categoryGroups`;

interface CategoryPayload {
  name: string;
  description: string;
  parentId?: number;
  categoryTypeId: number;
  slug?: string;
}

export interface CreateCategoryPayload extends CategoryPayload {}

const normalizeCategoryListResponse = (data: Category[] | CategoriesResponse): CategoriesResponse => {
  if (Array.isArray(data)) {
    return {
      data,
      meta: {
        count: data.length,
      },
    };
  }

  return data;
};

const normalizeCategoryDetailResponse = (data: Category | CategoryDetailResponse): CategoryDetailResponse => {
  if ('data' in data) {
    return data;
  }

  return { data };
};

const createCategorySlug = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100) || `category-${Date.now()}`;

export const createCategory = async (data?: CreateCategoryPayload) => {
  const response = await axios.request<Category>({
    url: categoryV2BasePath,
    method: HttpMethods.POST,
    data: data ? { ...data, slug: data.slug || createCategorySlug(data.name) } : data,
  });
  return normalizeCategoryDetailResponse(response.data);
};

export const getCategories = async (params?: CategoriesParams) => {
  const response = await axios.request<Category[] | CategoriesResponse>({
    url: categoryV2BasePath,
    method: HttpMethods.GET,
    params,
  });
  return normalizeCategoryListResponse(response.data);
};

export const getCategory = async (id: number) => {
  const response = await axios.request<Category | CategoryDetailResponse>({
    url: `${categoryV2BasePath}/${id}`,
    method: HttpMethods.GET,
  });
  return normalizeCategoryDetailResponse(response.data);
};

export interface UpdateCategoryPayload {
  name: string;
  description: string;
  parentId: number;
  categoryTypeId: number;
}

export interface UpdateCategoryService {
  id: number;
  data: Partial<UpdateCategoryPayload>;
}

export const updateCategory = async ({ id, data }: UpdateCategoryService) => {
  const response = await axios.request<Category | CategoryDetailResponse>({
    url: `${categoryV2BasePath}/${id}`,
    method: HttpMethods.PATCH,
    data,
  });
  return normalizeCategoryDetailResponse(response.data);
};

export const getCategoriesGroup = async () => {
  const response = await axios.request<CategoriesGroupResponse>({
    url: `${categoryGroupBasePath}`,
    method: HttpMethods.GET,
  });
  return response.data;
};

export const getCategoriesSubgroup = async (params: { [key: string]: string | number | undefined }) => {
  const response = await axios.request({
    url: `${categoryGroupBasePath}/subgroup`,
    method: HttpMethods.GET,
    params,
  });
  return response.data;
};

export const getCategoriesByGroup = async (id: number) => {
  const response = await axios.request({
    url: `${categoryBasePath}/${id}`,
    method: HttpMethods.GET,
  });
  return response.data;
};

export const postDefaultCategories = async () => {
  const res = await axios.request({
    method: HttpMethods.POST,
    url: `${categoryBasePath}/default`,
  });
  return res.data;
};
